/** Trader Crypto Pro: the client's Pro wallet overview, opening it, and moving money with Live. */
import { useEffect } from 'react';
import type { ProOverview, ProTransfer, ProTransferDirection } from '@platform/shared';
import { api } from '../lib/api';
import { createStore, useStore } from '../lib/store';
import { loadMe, useMe } from './session';

interface ProState {
  data: ProOverview | null;
  loading: boolean;
  error: string | null;
}

export const proStore = createStore<ProState>({ data: null, loading: false, error: null });

export async function reloadPro() {
  proStore.set({ loading: true });
  try {
    proStore.set({ data: await api.get<ProOverview>('/api/pro'), loading: false, error: null });
  } catch (e) {
    proStore.set({ loading: false, error: (e as Error).message });
  }
}

export async function openPro() {
  proStore.set({ data: await api.post<ProOverview>('/api/pro/open') });
}

/** Resolves to the final transfer (confirmed or failed); refreshes the wallet and the Live balance. */
export async function moveFunds(direction: ProTransferDirection, amount: string): Promise<ProTransfer> {
  const t = await api.post<ProTransfer>('/api/pro/transfers', { direction, amount }, { idempotencyKey: crypto.randomUUID() });
  await Promise.all([reloadPro(), loadMe().catch(() => {})]);
  return t;
}

/** Load once when a screen that needs it mounts. */
export function usePro() {
  useEffect(() => {
    void reloadPro();
  }, []);
  return useStore(proStore, (s) => s);
}

/**
 * Whether to show the Crypto Pro menu item: as the API says for this client (`shown`), and always
 * for admins. Loads the overview once per signed-in user.
 */
export function useShowPro(): boolean {
  const me = useMe();
  const id = me?.id ?? null;
  useEffect(() => {
    if (id) void reloadPro();
    else proStore.set({ data: null });
  }, [id]);
  const data = useStore(proStore, (s) => s.data);
  if (!me) return false;
  return me.role === 'admin' || me.role === 'dealer' || !!data?.shown;
}
